import React, { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { Row, Col } from 'antd'
import { CallToAction, Element } from '../Elements'
import './styles.less'
import { JOBS, PARTNERS, PRINCIPLES } from './data'
import SuperText from '../Elements/SuperText'
import {
  GlobeMark,
  GroupMark,
  ReduceMark,
  RiseMark,
  ShieldMark,
  SparkMark,
} from './ServiceIcons'

// One mark per service, keyed by job id. Kept here rather than in data.js so
// that file stays plain data with no React imports. These replaced the
// single-colour @ant-design/icons glyphs - which also drops a dependency we
// only ever had transitively through antd.
const JOB_ICONS = {
  start: SparkMark, // where do we even begin
  stall: GroupMark, // getting past the keen few
  deep: RiseMark, // real productivity in one function
  impact: GlobeMark, // what is our AI costing the planet
  rightsize: ReduceMark, // shrink the footprint and the bill
  rules: ShieldMark, // rules and governance
}

// Contentful-rich-text shape the Title element expects ({ raw: <JSON> }); the
// bold text nodes are what pick up the site's underline highlight, exactly
// like the ai-and-us hero.
const heroTitleRichText = {
  raw: JSON.stringify({
    nodeType: 'document',
    data: {},
    content: [
      {
        nodeType: 'heading-1',
        data: {},
        content: [
          {
            nodeType: 'text',
            value: 'AI that’s good for your people',
            marks: [],
            data: {},
          },
          {
            nodeType: 'text',
            value: ', and honest about the ',
            marks: [],
            data: {},
          },
          {
            nodeType: 'text',
            value: 'planet',
            marks: [{ type: 'bold' }],
            data: {},
          },
          { nodeType: 'text', value: '.', marks: [], data: {} },
        ],
      },
    ],
  }),
}

// Same {raw: <JSON>} shape as the title above. Italics on "to" and "with"
// are `italic` marks, which the default mark renderer turns into <i>.
const heroBodyRichText = {
  raw: JSON.stringify({
    nodeType: 'document',
    data: {},
    content: [
      {
        nodeType: 'paragraph',
        data: {},
        content: [
          {
            nodeType: 'text',
            value: 'Most AI rollouts are done ',
            marks: [],
            data: {},
          },
          {
            nodeType: 'text',
            value: 'to',
            marks: [{ type: 'italic' }],
            data: {},
          },
          {
            nodeType: 'text',
            value: ' teams rather than ',
            marks: [],
            data: {},
          },
          {
            nodeType: 'text',
            value: 'with',
            marks: [{ type: 'italic' }],
            data: {},
          },
          {
            nodeType: 'text',
            value:
              ' them. Everyone’s racing to implement quickly, but nobody’s tracking what it costs. We do things the other way round, helping build governance and processes that are better for people, planet and (yes) your bottom line.',
            marks: [],
            data: {},
          },
        ],
      },
    ],
  }),
}

const Eyebrow = ({ children, className }) => (
  <span className={classNames('aia-eyebrow', className)}>{children}</span>
)

// Both hero CTAs leave the site, so CustomLink renders them as real anchors
// with target="_blank" (its default for external urls).
// Drives all seven Book a call buttons on this page: the hero CTA and one per
// service block.
const BOOKING_URL = 'https://meet.brevo.com/timo-muller-1/ai-services'
const SURVEY_URL = 'https://app.lfca.earth/public/emotion-survey'

const HERO_ACTIONS = [
  { text: 'Book a call', type: 'primary', url: BOOKING_URL },
  { text: 'Community survey', type: 'link', url: SURVEY_URL },
]

// Pre-optimised in static/images/ai-services: webp at three widths
// (555/740/1110). No png fallback - alpha-webp is supported by every browser
// we target, and <picture> earns nothing when there is only one format.
// The intrinsic 1110x1062 on the <img> reserves the aspect ratio so the hero
// does not shift as it loads; it is the LCP image, hence eager + high priority.
const HERO_SRC = '/images/ai-services/hero'
const HERO_ALT =
  'An LFCA AI workshop in progress: a team dot-voting on an emotion wheel in a shared board, with participants on video alongside'

const HeroImage = () => (
  <img
    alt={HERO_ALT}
    className="aia-hero-img"
    decoding="async"
    fetchpriority="high"
    height={1062}
    loading="eager"
    sizes="(max-width: 767px) 92vw, (max-width: 1199px) 46vw, 580px"
    src={`${HERO_SRC}-740.webp`}
    srcSet={`${HERO_SRC}-555.webp 555w, ${HERO_SRC}-740.webp 740w, ${HERO_SRC}-1110.webp 1110w`}
    width={1110}
  />
)

// --- 1 · hero (chalk, two columns: copy left, workshop image right) -------
const HeroSection = () => (
  <section className="aia-hero">
    <div className="container">
      <Row align="middle" className="aia-hero-row">
        <Col className="aia-hero-copy" lg={14} md={13} xs={24}>
          <Element
            actions={HERO_ACTIONS}
            body={heroBodyRichText}
            className="aia-hero-element"
            layout="title"
            superText="AI Services"
            titleRichText={heroTitleRichText}
          />
        </Col>
        <Col className="aia-hero-media" lg={10} md={11} xs={24}>
          <HeroImage />
        </Col>
      </Row>
    </div>
  </section>
)

// --- 2 · partners (short trust strip: title + four logo wrappers) ---------
// 5-col grid on desktop: the title takes 8 of antd's 24 columns and the four
// logos 4 each (8 + 4*4 = 24). On mobile the title goes full width and the
// logos fall into a 2x2.
const PartnersSection = () => (
  <section className="aia-partners">
    <div className="container">
      <Row align="middle" className="aia-partners-row">
        <Col lg={8} md={8} xs={24}>
          <h2 className="aia-partners-title">
            Teams and partners we work with
          </h2>
        </Col>
        {PARTNERS.map((partner) => (
          <Col key={partner.id} lg={4} md={4} xs={12}>
            <div
              className={classNames('aia-logo-wrap', `is-${partner.variant}`)}
            >
              <img
                alt={partner.name}
                decoding="async"
                height={partner.height}
                loading="lazy"
                src={`/logos/partners/${partner.file}.webp`}
                width={partner.width}
              />
            </div>
          </Col>
        ))}
      </Row>
    </div>
  </section>
)

// Renders the service's real image once data.js has one, and falls back to the
// labelled placeholder box for the services still waiting on artwork.
const BlockMedia = ({ job }) => {
  const { image } = job

  return (
    <div className="aia-col-media">
      {image ? (
        <img
          alt={image.alt}
          className="aia-media-img"
          decoding="async"
          height={image.height}
          loading="lazy"
          sizes="(max-width: 767px) 88vw, 44vw"
          src={`${image.src}-${image.defaultWidth}.webp`}
          srcSet={image.widths
            .map((w) => `${image.src}-${w}.webp ${w}w`)
            .join(', ')}
          width={image.width}
        />
      ) : (
        <div className="aia-media-box">
          <span className="aia-media-label">{job.media}</span>
        </div>
      )}
    </div>
  )
}

// --- 3 · services (bubbles + sticky bar + alternating blocks) --------------
const ServicesSection = () => {
  const [active, setActive] = useState(-1)
  const blockRefs = useRef([])
  const chipRefs = useRef([])

  const go = (i) => {
    const el = blockRefs.current[i]
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    let raf = null
    const measure = () => {
      raf = null
      const marker = window.innerHeight * 0.42
      let current = -1
      blockRefs.current.forEach((b, i) => {
        if (b && b.getBoundingClientRect().top <= marker) current = i
      })
      setActive(current)
    }
    const onScroll = () => {
      if (raf) return
      raf = window.requestAnimationFrame(measure)
    }
    measure()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
      if (raf) window.cancelAnimationFrame(raf)
    }
  }, [])

  // keep the active chip in view within the horizontally-scrolling bar
  useEffect(() => {
    const chip = chipRefs.current[active]
    if (chip) chip.scrollIntoView({ inline: 'nearest', block: 'nearest' })
  }, [active])

  return (
    <section className="aia-services">
      <div className="aia-container aia-services-intro">
        <SuperText text="Every team’s stuck somewhere" />
        {/* `rich-text-title pink` is the site's underline treatment: the
            mixin draws the pink underline svg behind any <b> inside it. */}
        <div className="rich-text-title pink">
          <h2 className="aia-services-title">
            What’s the AI question you can’t <b>answer yet</b>?
          </h2>
        </div>
        <p className="aia-services-sub">
          Pick the one that sounds like you - we’ll show you exactly how we’d
          help.
        </p>
        <div className="aia-bubbles">
          {JOBS.map((job, i) => {
            const Icon = JOB_ICONS[job.id]
            return (
              <button
                type="button"
                className="aia-bubble"
                key={job.id}
                onClick={() => go(i)}
              >
                <span aria-hidden="true" className="aia-bubble-icon">
                  {Icon ? <Icon /> : null}
                </span>
                <span className="aia-bubble-text">
                  {/* <span className="aia-btag">{job.tag}</span> */}
                  {job.q}
                </span>
              </button>
            )
          })}
        </div>
      </div>

      <div className="aia-bar">
        <div className="aia-container aia-bar-inner">
          <span className="aia-bar-title">How we can help</span>
          <div className="aia-chips">
            {JOBS.map((job, i) => (
              <button
                type="button"
                key={job.id}
                ref={(el) => {
                  chipRefs.current[i] = el
                }}
                className={classNames('aia-chip', i === active && 'is-on')}
                onClick={() => go(i)}
              >
                {job.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="aia-blocks">
        {JOBS.map((job, i) => (
          <section
            className={classNames('aia-block', i % 2 === 1 && 'is-flip')}
            key={job.id}
            ref={(el) => {
              blockRefs.current[i] = el
            }}
          >
            <div className="aia-block-inner">
              <div className="aia-col-text">
                <span className="aia-block-head">
                  <span aria-hidden="true" className="aia-block-icon">
                    {JOB_ICONS[job.id]
                      ? React.createElement(JOB_ICONS[job.id])
                      : null}
                  </span>
                  {/* a span, not the <p> this used to be: it now sits inside
                      .aia-block-head, and a <p> inside a <span> is invalid */}
                  <span className="aia-q-echo">{job.q}</span>
                </span>
                <h3 className="aia-block-title">{job.title}</h3>
                {job.body.map((para) => (
                  <p key={para.slice(0, 24)}>{para}</p>
                ))}
                <ul className="aia-includes">
                  {job.includes.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <div className="aia-block-cta">
                  <CallToAction
                    cta={{
                      text: 'Book a call',
                      type: 'primary',
                      url: BOOKING_URL,
                      size: 'large',
                    }}
                    className="aia-cta-purple"
                  />
                  {job.showSurveyCta && (
                    // `type: 'ghost'` is the one type CallToAction handles
                    // specially - it passes antd type="default" plus the ghost
                    // flag, which is a transparent button with a border.
                    <CallToAction
                      cta={{
                        text: 'Community survey',
                        type: 'ghost',
                        url: SURVEY_URL,
                        size: 'large',
                      }}
                      className="aia-cta-ghost"
                    />
                  )}
                </div>
              </div>
              <BlockMedia job={job} />
            </div>
          </section>
        ))}
      </div>
    </section>
  )
}

// --- 4 - how we work (dark purple) + the closing CTA ----------------------
const PrinciplesSection = () => (
  <section className="aia-principles">
    <div className="container">
      <div className="aia-principles-head">
        <SuperText text="How we work" />
        <h2 className="aia-principles-title">
          What you’ll notice working with us
        </h2>
      </div>

      <Row className="aia-principles-row" gutter={[32, 44]}>
        {PRINCIPLES.map((p) => (
          <Col key={p.id} lg={6} md={12} xs={24}>
            <div className="aia-principle">
              <h3 className="aia-principle-title">{p.title}</h3>
              <p>{p.body}</p>
              <span className="aia-see-lab">You’ll see it in</span>
              <ul className="aia-see-list">
                {p.seeItIn.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          </Col>
        ))}
      </Row>

      {/* the site's default primary (yellow) rather than the purple used on the
          service cards: a purple button only separates from this background at
          2:1, where yellow gets 6.1:1 */}
      <div className="aia-principles-cta">
        <CallToAction
          cta={{
            text: 'Book a call',
            type: 'primary',
            url: BOOKING_URL,
            size: 'large',
          }}
        />
      </div>
    </div>
  </section>
)

export const AiApproach = () => (
  <div className="ai-approach">
    <HeroSection />
    <PartnersSection />
    <ServicesSection />
    <PrinciplesSection />
  </div>
)

export default AiApproach
